/**
 * Per-device conveniences only. Every access is guarded: storage can be
 * unavailable (private mode, blocked site data) and the app must still work.
 */

function read<T>(store: Storage | undefined, key: string, fallback: T): T {
  try {
    const raw = store?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(store: Storage | undefined, key: string, value: unknown): void {
  try {
    if (value === undefined) store?.removeItem(key);
    else store?.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: the feature degrades, booking still works */
  }
}

const local = () => (typeof localStorage === "undefined" ? undefined : localStorage);
const session = () => (typeof sessionStorage === "undefined" ? undefined : sessionStorage);

export interface SavedBooking {
  token: string;
  startsAt: string;
  serviceName: string;
  savedAt: string;
}

/** The customer's own booking links on this device, per studio. */
export const myBookings = {
  list(slug: string): SavedBooking[] {
    return read<SavedBooking[]>(local(), `my-bookings:${slug}`, []);
  },
  save(slug: string, b: SavedBooking) {
    const list = myBookings.list(slug).filter((x) => x.token !== b.token);
    write(local(), `my-bookings:${slug}`, [b, ...list].slice(0, 20));
  },
  remove(slug: string, token: string) {
    write(local(), `my-bookings:${slug}`, myBookings.list(slug).filter((x) => x.token !== token));
  },
};

/** Remember the customer's contacts to prefill the next booking (their own device). */
export const contactDraft = {
  get(slug: string): { name: string; phone: string; email: string } {
    return read(local(), `contacts:${slug}`, { name: "", phone: "", email: "" });
  },
  set(slug: string, v: { name: string; phone: string; email: string }) {
    write(local(), `contacts:${slug}`, v);
  },
};

/**
 * Idempotency keys survive a reload within the tab, so a retried submission of
 * the same draft reuses the key and gets the same booking and access token back.
 */
export const pendingKeys = {
  get(scope: string, fingerprint: string): string {
    const all = read<Record<string, string>>(session(), "idem-keys", {});
    const id = `${scope}:${fingerprint}`;
    if (!all[id]) {
      all[id] = crypto.randomUUID();
      write(session(), "idem-keys", all);
    }
    return all[id]!;
  },
  clear(scope: string, fingerprint: string) {
    const all = read<Record<string, string>>(session(), "idem-keys", {});
    delete all[`${scope}:${fingerprint}`];
    write(session(), "idem-keys", all);
  },
  clearAll() {
    write(session(), "idem-keys", undefined);
  },
};
